import { useId, useState, type FormEvent } from 'react';
import { DESCRIPTIONS_ROLE, LIBELLES_ROLE, LONGUEUR_MOT_DE_PASSE_MIN, type Apparence } from '@rh/shared';
import { ErreurReseau, changerApparence, changerMotDePasse } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { ChoixApparence } from '../composants/ChoixApparence';
import { Erreur } from '../composants/Etat';
import { appliquerApparence } from '../theme';

/** Mon compte : identité, rôle et changement de son propre mot de passe (tous les rôles). */
export function MonCompte() {
  const id = useId();
  const { utilisateur } = useAuth();
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState(false);
  /** Choix fait ici, en attendant que le compte relu le rapporte. */
  const [apparence, setApparence] = useState<Apparence | null>(null);
  const [erreurApparence, setErreurApparence] = useState<string | null>(null);

  if (!utilisateur) return null;
  const apparenceActuelle = apparence ?? utilisateur.apparence;

  /** Appliqué tout de suite, enregistré ensuite ; un refus remet le style précédent. */
  async function choisirApparence(choix: Apparence): Promise<void> {
    const precedente = apparenceActuelle;
    if (choix === precedente) return;
    setApparence(choix);
    appliquerApparence(choix);
    setErreurApparence(null);
    try {
      await changerApparence(choix);
    } catch (e) {
      setApparence(precedente);
      appliquerApparence(precedente);
      setErreurApparence(
        e instanceof ErreurReseau
          ? 'Style non enregistré : pas de réseau. Réessayez une fois en ligne.'
          : e instanceof Error
            ? e.message
            : 'Changement impossible',
      );
    }
  }

  async function changer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    setFait(false);
    if (!actuel) return setErreur('Saisissez votre mot de passe actuel');
    if (nouveau.length < LONGUEUR_MOT_DE_PASSE_MIN) {
      return setErreur(`Le nouveau mot de passe doit faire au moins ${LONGUEUR_MOT_DE_PASSE_MIN} caractères`);
    }
    if (nouveau !== confirmation) return setErreur('La confirmation ne correspond pas au nouveau mot de passe');
    if (nouveau === actuel) return setErreur('Le nouveau mot de passe doit être différent de l’actuel');
    setEnvoi(true);
    setErreur(null);
    try {
      await changerMotDePasse(actuel, nouveau);
      setActuel('');
      setNouveau('');
      setConfirmation('');
      setFait(true);
    } catch (e) {
      setErreur(
        e instanceof ErreurReseau
          ? 'Réponse non reçue : le réseau a coupé. Si le changement a abouti, reconnectez-vous avec le nouveau mot de passe ; sinon, réessayez une fois en ligne.'
          : e instanceof Error
            ? e.message
            : 'Changement impossible',
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <section>
      <div className="entete-annonce">
        <h2>Mon compte</h2>
      </div>
      <p>
        <strong>{utilisateur.nom}</strong> · {utilisateur.email}
      </p>
      <p className="mention">
        Rôle : <strong>{LIBELLES_ROLE[utilisateur.role]}</strong> — {DESCRIPTIONS_ROLE[utilisateur.role]}
      </p>

      <section className="bloc">
        <h3>Style d’affichage</h3>
        <p className="mention">Il suit votre compte sur tous vos appareils.</p>
        <ChoixApparence nom={`${id}-apparence`} valeur={apparenceActuelle} onChange={(choix) => void choisirApparence(choix)} />
        {erreurApparence && <Erreur message={erreurApparence} />}
      </section>

      <section className="bloc">
        <h3>Changer mon mot de passe</h3>
        <p className="mention">
          Au moins {LONGUEUR_MOT_DE_PASSE_MIN} caractères ; une phrase facile à retenir convient. Vos autres appareils
          seront déconnectés.
        </p>
        <form className="formulaire-admin" noValidate onSubmit={(e) => void changer(e)}>
          <div className="champ pleine">
            <label htmlFor={`${id}-actuel`}>Mot de passe actuel</label>
            <input
              id={`${id}-actuel`}
              type="password"
              autoComplete="current-password"
              value={actuel}
              onChange={(e) => setActuel(e.target.value)}
            />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-nouveau`}>Nouveau mot de passe</label>
            <input
              id={`${id}-nouveau`}
              type="password"
              autoComplete="new-password"
              value={nouveau}
              onChange={(e) => setNouveau(e.target.value)}
            />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-confirmation`}>Confirmation</label>
            <input
              id={`${id}-confirmation`}
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>
          <div className="boutons">
            <button type="submit" className="bouton" disabled={envoi}>
              Changer le mot de passe
            </button>
          </div>
        </form>
        {erreur && <Erreur message={erreur} />}
        {fait && (
          <p className="notice-cv" role="status">
            Mot de passe changé. Vos autres appareils sont déconnectés.
          </p>
        )}
      </section>
    </section>
  );
}
